import React, { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import type { WorkOrderTemplate } from '#/graphql/types';
import useWOTemplateQuery from '../hooks/useWOTemplateQuery';
import WOTemplateForm from '../components/WOTemplateForm';
import useWOTemplateUpdate from '../hooks/useWOTemplateUpdate';
import useWOTemplateDelete from '../hooks/useWOTemplateDelete';
import { updateFields } from '../components/WOTemplateForm/fields';
import { SubmitWorkOrderTemplate } from '../utils/types';
import getFormTitle from '#/common/utils/getFormTitle';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';

export interface WOTemplateParams {
  readonly id?: string;
}

const WOTemplatePage: React.FC = () => {
  const { id } = useParams() as WOTemplateParams;
  const woTemplateQuery = useWOTemplateQuery(id);
  const woTemplate = woTemplateQuery.data?.getWorkOrderTemplate?.data;
  const isNotFound = useNotFoundGuard({ loading: woTemplateQuery.loading, id, entity: woTemplate });

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network). Only updates after user's own save.
  const formKeyRef = useRef<string | null>(null);
  if (woTemplate && !formKeyRef.current) {
    formKeyRef.current = woTemplate.updatedAt ?? woTemplate.id ?? null;
  }

  const woTemplateNumber = woTemplate?.displayNumber;
  const title = getFormTitle(id, woTemplateNumber, 'WO Template');

  // Update Operation
  const { updateWOTemplate } = useWOTemplateUpdate();
  const updateWoTemplate = useCallback<SubmitWorkOrderTemplate>(
    async (woTemplate: WorkOrderTemplate) => {
      if (!id) {
        return undefined;
      }

      const { data, errors } = (await updateWOTemplate(id, woTemplate)) as {
        data?: WorkOrderTemplate;
        errors?: string[];
      };

      if (data?.updatedAt && !errors?.length) {
        formKeyRef.current = data.updatedAt;
      }

      return {
        data: data ?? undefined,
        errors: errors ?? undefined,
      };
    },
    [id, updateWOTemplate],
  );

  // Delete Operation
  const { deleteWOTemplate: delWOTemplate } = useWOTemplateDelete();
  const deleteWOTemplate = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return delWOTemplate(id);
  }, [delWOTemplate, id]);

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="form" isLoading={false}>
        <RecordNotFound backUrl="/work-orders/templates" />
      </SubPage>
    );
  }

  return (
    <SubPage
      type="form"
      title={title || 'Work Order Template'}
      isLoading={woTemplateQuery.loading}
      showGoBackButton>
      {woTemplate && (
        <WOTemplateForm
          key={`wotemplate-${id}-${formKeyRef.current}`}
          title={title}
          woTemplate={woTemplate}
          fields={updateFields}
          submit={updateWoTemplate}
          deleteWoTemplate={deleteWOTemplate}
        />
      )}
    </SubPage>
  );
};

export default React.memo(WOTemplatePage);
